/**
 * @module services/catalogs/utils
 * @description Shared utility functions for catalog services
 */

import type { ItemData, ItemField, CreatorType } from '@types';
import {
  getCreatorTypesForItemType,
  getDefaultCreatorType,
  getFieldsForItemType,
} from '@/data/itemTypes';
import { LANGUAGE_MAP, LANGUAGE_FULL_NAMES, SYSTEM_FIELDS } from './types';

/**
 * French articles and prepositions that should remain lowercase
 * (except when they are the first word)
 */
const FRENCH_LOWERCASE_WORDS = new Set([
  'le',
  'la',
  'les',
  'l',
  'de',
  'du',
  'des',
  'd',
  'à',
  'au',
  'aux',
  'en',
  'et',
  'ou',
  'sur',
  'pour',
  'par',
  'avec',
  'sans',
  'sous',
]);

/**
 * Capitalize a publisher name properly
 * Handles all-uppercase, all-lowercase, and mixed case
 * Keeps French articles and prepositions lowercase (except first word)
 * Removes leading "Les" from "Les Éditions..." patterns
 * Examples:
 * - "entremonde" → "Entremonde"
 * - "ENTREMONDE" → "Entremonde"
 * - "les éditions de minuit" → "Éditions de Minuit"
 * - "GALLIMARD" → "Gallimard"
 * - "presses universitaires de france" → "Presses Universitaires de France"
 * @param publisher Raw publisher name
 * @returns Properly capitalized publisher name
 */
export function capitalizePublisher(publisher: string): string {
  if (!publisher) return publisher;

  let trimmed = publisher.trim();
  if (!trimmed) return trimmed;

  // Remove leading "Les" from "Les Éditions..." or "Les Editions..." patterns
  trimmed = trimmed.replace(/^les\s+[ée]ditions?\s+/i, (match) => {
    // Keep "Éditions" or "Editions" but remove "Les "
    return match.replace(/^les\s+/i, '');
  });

  // Check if all uppercase or all lowercase
  const isAllUpper = trimmed === trimmed.toUpperCase() && trimmed !== trimmed.toLowerCase();
  const isAllLower = trimmed === trimmed.toLowerCase() && trimmed !== trimmed.toUpperCase();

  if (isAllUpper || isAllLower) {
    // Convert to proper case with French rules
    const words = trimmed.toLowerCase().split(/(\s+)/);
    let isFirst = true;

    return words
      .map((word) => {
        if (word.trim() === '') return word; // Preserve whitespace

        // Handle words with apostrophe (l', d')
        const baseWord = word.replace(/[''].*$/, '');

        if (isFirst) {
          isFirst = false;
          return word.charAt(0).toUpperCase() + word.slice(1);
        }

        // Keep articles and prepositions lowercase
        if (FRENCH_LOWERCASE_WORDS.has(baseWord)) {
          return word;
        }

        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('');
  }

  // Mixed case - just ensure first letter is uppercase
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Map a language code to ISO 639-1 format
 * @param code Language code (ISO 639-3, name, etc.)
 * @returns Mapped language code or original if not found
 */
export function mapLanguageCode(code: string): string {
  const lowerCode = code.toLowerCase().trim();
  return LANGUAGE_MAP[lowerCode] || code;
}

/**
 * Map a language code to its full name
 * @param code Language code (ISO 639-3)
 * @returns Full language name or original code if not found
 */
export function mapLanguageToFullName(code: string): string {
  const lowerCode = code.toLowerCase().trim();
  return LANGUAGE_FULL_NAMES[lowerCode] || code;
}

/**
 * Validate and fix creator types to ensure they are valid for the item type
 * Modifies the item in place
 * @param item The item to validate
 * @param serviceName Optional service name for logging
 */
export function validateCreatorTypes(item: Partial<ItemData>, serviceName?: string): void {
  if (!item.creators || !item.itemType) return;

  const validTypes = getCreatorTypesForItemType(item.itemType);
  const defaultType = getDefaultCreatorType(item.itemType);

  item.creators = item.creators.map((creator) => {
    if (!validTypes.includes(creator.creatorType)) {
      // Try to map to a similar valid type
      let newType = defaultType;

      if (creator.creatorType === 'director' && validTypes.includes('contributor')) {
        newType = 'contributor';
      } else if (creator.creatorType === 'editor' && validTypes.includes('editor')) {
        newType = 'editor';
      } else if (creator.creatorType === 'translator' && validTypes.includes('translator')) {
        newType = 'translator';
      }

      return { ...creator, creatorType: newType };
    }
    return creator;
  });
}

/**
 * Validate and remove fields that are not valid for the item type
 * Modifies the item in place
 * @param item The item to validate
 * @param serviceName Optional service name for logging
 */
export function validateFields(item: Partial<ItemData>, serviceName?: string): void {
  if (!item.itemType) return;

  const validFields = getFieldsForItemType(item.itemType);
  const itemKeys = Object.keys(item) as (keyof ItemData)[];

  for (const key of itemKeys) {
    // Skip system fields and valid fields
    if (
      SYSTEM_FIELDS.includes(key as (typeof SYSTEM_FIELDS)[number]) ||
      validFields.includes(key as ItemField)
    ) {
      continue;
    }

    // Remove invalid field if it has a value
    if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      delete item[key];
    }
  }
}

/**
 * Extract page count from a format/extent string
 * @param text Text containing page information (e.g., "234 p.", "x, 456 pages")
 * @returns Page count as string, or null if not found
 */
export function extractPageCount(text: string): string | null {
  const pageMatch = text.match(/(\d+)\s*p/i);
  return pageMatch ? pageMatch[1] : null;
}

/**
 * Extract year from a date string
 * @param date Date string in various formats
 * @returns Four-digit year, or original string if no year found
 */
export function extractYear(date: string): string {
  const yearMatch = date.match(/\d{4}/);
  return yearMatch ? yearMatch[0] : date;
}

/**
 * Clean UNIMARC control characters from text
 * Removes non-sorting indicators (U+0098, U+009C, U+0088, U+0089)
 * @param text Text to clean
 * @returns Cleaned text or null if input is null/undefined
 */
export function cleanUnimarcText(text: string | null | undefined): string | null {
  if (!text) return null;
  return text.replace(/[\u0098\u009C\u0088\u0089]/g, '').trim() || null;
}

/**
 * Parse a creator name string in "LastName, FirstName" format
 * @param name Full name string
 * @param defaultType Default creator type to use
 * @param itemType Item type for validation
 * @returns Creator object or null if name is empty
 */
export function parseCreatorName(
  name: string,
  defaultType: CreatorType,
  itemType: string
): { creatorType: CreatorType; lastName?: string; firstName?: string; name?: string } | null {
  if (!name || !name.trim()) return null;

  const cleanName = name.trim();

  // Validate creator type for item type
  const validTypes = getCreatorTypesForItemType(itemType);
  const creatorType = validTypes.includes(defaultType)
    ? defaultType
    : getDefaultCreatorType(itemType);

  // Parse name format: "LastName, FirstName" or "Name"
  if (cleanName.includes(',')) {
    const [lastName, firstName] = cleanName.split(',', 2);
    return {
      creatorType,
      lastName: lastName.trim(),
      firstName: firstName?.trim() || undefined,
    };
  } else {
    // Single name - could be corporate or single-name person
    return {
      creatorType,
      name: cleanName,
    };
  }
}

/**
 * Escape special characters in CQL query
 * @param text Text to escape
 * @returns Escaped text safe for CQL queries
 */
export function escapeCQL(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Delay helper for rate limiting
 * @param ms Milliseconds to wait
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Clean BnF creator name from dates and role suffixes
 * Removes patterns like:
 * - Life dates: "(1946-....)", "(1814-1876)", "(1899-2000)"
 * - Role suffixes: ". Auteur du texte", ". Éditeur scientifique", ". Traducteur"
 * Examples:
 * - "Bruno (1946-....). Auteur du texte" → "Bruno"
 * - "Michel (1814-1876). Auteur du texte" → "Michel"
 * - "Arthur (1899-2000). Éditeur scientifique" → "Arthur"
 * @param name Creator name to clean
 * @returns Object with cleaned name and detected role
 */
export function cleanBnfName(name: string): { cleanName: string; role?: string } {
  let cleanName = name;
  let role: string | undefined;

  // Extract role suffix after period: ". Auteur du texte", ". Éditeur scientifique", etc.
  const roleSuffixMatch = cleanName.match(
    /\.\s*(Auteur du texte|Éditeur scientifique|Traducteur|Illustrateur|Préfacier|Directeur de publication|Collaborateur|Compositeur|Interprète)$/i
  );
  if (roleSuffixMatch) {
    const roleText = roleSuffixMatch[1].toLowerCase();
    cleanName = cleanName.replace(roleSuffixMatch[0], '').trim();

    // Map French roles to creator types
    if (roleText.includes('auteur')) {
      role = 'author';
    } else if (roleText.includes('éditeur') || roleText.includes('directeur')) {
      role = 'editor';
    } else if (roleText.includes('traducteur')) {
      role = 'translator';
    } else if (roleText.includes('illustrateur')) {
      role = 'contributor';
    } else if (roleText.includes('préfacier') || roleText.includes('collaborateur')) {
      role = 'contributor';
    } else if (roleText.includes('compositeur')) {
      role = 'composer';
    } else if (roleText.includes('interprète')) {
      role = 'performer';
    }
  }

  // Remove life dates in parentheses: (1946-....), (1814-1876), (1899-2000), (19..-....), etc.
  cleanName = cleanName
    .replace(/\s*\(\d{4}-\.{0,4}\)/g, '') // (1946-....) or (1946-)
    .replace(/\s*\(\d{4}-\d{4}\)/g, '') // (1814-1876)
    .replace(/\s*\(\d{2}\.\.-\.{0,4}\)/g, '') // (19..-....)
    .replace(/\s*\(\d{2}\.\.-\d{4}\)/g, '') // (19..-1976)
    .trim();

  // Remove trailing period
  if (cleanName.endsWith('.')) {
    cleanName = cleanName.slice(0, -1).trim();
  }

  return { cleanName, role };
}

/**
 * Clean BnF publisher and extract place
 * Handles format "Publisher (City)" → { publisher: "Publisher", place: "City" }
 * Examples:
 * - "Entremonde (Lausanne)" → { publisher: "Entremonde", place: "Lausanne" }
 * - "Gallimard (Paris)" → { publisher: "Gallimard", place: "Paris" }
 * @param publisher Raw publisher string
 * @returns Object with cleaned publisher and optional place
 */
export function cleanBnfPublisher(publisher: string): { publisher: string; place?: string } {
  // Check for "Publisher (City)" pattern
  const placeMatch = publisher.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (placeMatch) {
    return {
      publisher: placeMatch[1].trim(),
      place: placeMatch[2].trim(),
    };
  }

  return { publisher: publisher.trim() };
}

/**
 * Clean Swisscovery creator name from various pollutants
 * Removes patterns like:
 * - Life dates: "Guy 1931-1994" → "Guy"
 * - Authority IDs: "(IDREF)026815672", "(DE-588)172944546"
 * - MARC codes: "880-01", "880-02"
 * - Role codes: "aut", "edt", "cre", "wpr", "author."
 * - Trailing periods (except for initials)
 * @param name Creator name to clean
 * @returns Cleaned name
 */
export function cleanSwisscoveryName(name: string): string {
  let cleaned = name
    // Remove authority IDs: (IDREF)xxx, (DE-588)xxx, etc.
    .replace(/\s*\([A-Z-]+\)\d+/g, '')
    // Remove MARC linking codes: 880-01, 880-02, etc.
    .replace(/\s+880-\d+/g, '')
    // Remove life dates: 1931-1994, 1968-, 1942-
    .replace(/\s+\d{4}-\d{0,4}/g, '')
    // Remove standalone role codes at end: aut, edt, cre, wpr, trl, etc.
    .replace(/\s+(aut|edt|cre|wpr|trl|ctb|pbl|ill|drt|pro|com)$/i, '')
    // Remove "author." at end
    .replace(/\s+author\.?$/i, '')
    .trim();

  // Remove trailing period unless it's an initial (single letter + period)
  if (cleaned.endsWith('.') && !/\s[A-Za-z]\.$/.test(cleaned)) {
    cleaned = cleaned.slice(0, -1).trim();
  }

  return cleaned;
}

/**
 * Clean Swisscovery title from non-filing indicators
 * Removes "<<" and ">>" markers used for non-filing characters
 * Examples:
 * - "<<Le>> démon de la politique" → "Le démon de la politique"
 * - "<<L'>>epoca del postumano" → "L'epoca del postumano"
 * @param title Title to clean
 * @returns Cleaned title
 */
export function cleanSwisscoveryTitle(title: string): string {
  return title.replace(/<<|>>/g, '').trim();
}

/**
 * Retry wrapper for async operations
 * @param fn Function to retry
 * @param options Retry options
 * @returns Result of the function
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    delayMs?: number;
    onRetry?: (attempt: number, error: Error) => void;
  } = {}
): Promise<T> {
  const { maxAttempts = 3, delayMs = 1000, onRetry } = options;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxAttempts) {
        onRetry?.(attempt, lastError);
        await delay(delayMs * attempt); // Exponential backoff
      }
    }
  }

  throw lastError;
}
